/**
 * Componente para capturar/subir evidencias fotográficas
 * 
 * Características:
 * - Captura desde cámara o selección de archivo
 * - Preview de imágenes antes de subir
 * - Múltiples imágenes
 * - Compresión automática
 * - Validación de tipo y tamaño
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Card,
  CardMedia,
  CardActions,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import evidenciasService from '../services/evidenciasService';

interface ImagePreview {
  file: File;
  preview: string;
  compressed?: boolean;
}

interface CameraCaptureProps {
  auditoriaId: number;
  preguntaId?: number;
  onUploadSuccess?: (evidencias: any[]) => void;
  onUploadError?: (error: string) => void;
  maxImages?: number;
  autoCompress?: boolean;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({
  auditoriaId,
  preguntaId,
  onUploadSuccess,
  onUploadError,
  maxImages = 10,
  autoCompress = true,
}) => {
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [compressing, setCompressing] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Manejar selección de archivos
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setError(null);
    const newImages: ImagePreview[] = [];

    // Verificar límite de imágenes
    if (images.length + files.length > maxImages) {
      setError(`Máximo ${maxImages} imágenes permitidas`);
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validar archivo
      const validation = evidenciasService.validateFile(file);
      if (!validation.valid) {
        setError(validation.error || 'Archivo inválido');
        continue;
      }

      // Comprimir si está habilitado
      let processedFile = file;
      let compressed = false;

      if (autoCompress) {
        try {
          setCompressing(true);
          processedFile = await evidenciasService.compressImage(file);
          compressed = true;
        } catch (err) {
          console.warn('No se pudo comprimir imagen, usando original:', err);
        } finally {
          setCompressing(false);
        }
      }

      // Crear preview
      const preview = URL.createObjectURL(processedFile);
      newImages.push({ file: processedFile, preview, compressed });
    }

    setImages(prev => [...prev, ...newImages]);

    // Resetear input
    if (event.target) {
      event.target.value = '';
    }
  };

  // Eliminar imagen del preview
  const handleRemoveImage = (index: number) => {
    setImages(prev => {
      const updated = [...prev];
      // Liberar URL del preview
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  // Subir imágenes
  const handleUpload = async () => {
    if (images.length === 0) {
      setError('Selecciona al menos una imagen');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const result = await evidenciasService.upload({
        auditoria_id: auditoriaId,
        pregunta_id: preguntaId,
        imagenes: images.map(img => img.file),
      });

      // Limpiar previews
      images.forEach(img => URL.revokeObjectURL(img.preview));
      setImages([]);

      // Callback de éxito
      if (onUploadSuccess) {
        onUploadSuccess(result.evidencias);
      }

    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.response?.data?.details || 'Error al subir imágenes';
      setError(errorMsg);
      if (onUploadError) {
        onUploadError(errorMsg);
      }
    } finally {
      setUploading(false);
    }
  };

  // Calcular tamaño total
  const getTotalSize = () => {
    const total = images.reduce((sum, img) => sum + img.file.size, 0);
    return evidenciasService.formatFileSize(total);
  };

  return (
    <Box>
      {/* Botones de captura - Siempre visibles */}
      <Box sx={{ 
        display: 'flex', 
        gap: { xs: 1, sm: 2 }, 
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <Button
          variant="contained"
          startIcon={<CameraIcon />}
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading || compressing || images.length >= maxImages}
          fullWidth
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            py: { xs: 1, sm: 1.25 }
          }}
        >
          Tomar Foto
        </Button>

        <Button
          variant="outlined"
          startIcon={<ImageIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || compressing || images.length >= maxImages}
          fullWidth
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            py: { xs: 1, sm: 1.25 }
          }}
        >
          Adjuntar Archivo
        </Button>

        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>

      {/* Estado de compresión */}
      {compressing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Comprimiendo imagen...</Typography>
          </Box>
        </Alert>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Previews de imágenes */}
      {images.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">
              {images.length} imagen{images.length !== 1 ? 'es' : ''} seleccionada{images.length !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total: {getTotalSize()}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {images.map((image, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    height="140"
                    image={image.preview}
                    alt={`Preview ${index + 1}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardActions sx={{ justifyContent: 'space-between', p: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                        {image.file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {evidenciasService.formatFileSize(image.file.size)}
                      </Typography>
                      {image.compressed && (
                        <Chip label="Comprimida" size="small" color="success" sx={{ height: 16, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveImage(index)}
                        disabled={uploading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Botón de subir */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
              onClick={handleUpload}
              disabled={uploading || images.length === 0}
            >
              {uploading ? 'Subiendo...' : `Subir ${images.length} imagen${images.length !== 1 ? 'es' : ''}`}
            </Button>
          </Box>
        </Box>
      )}

      {/* Info */}
      {images.length === 0 && !compressing && (
        <Box sx={{ py: 0.75, px: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
            Máximo {maxImages} imágenes, 5MB cada una
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CameraCapture;
